import { useAppDispatch, useAppSelector } from '@app/store';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { RecordingPlaybackState } from '../../../domain';
import {
  selectAspectRatio,
  selectRecordingPlayback,
  selectSelectedClip,
} from '../selector';

export interface ClipPlaybackState {
  isClipMode: boolean;
  clipStartTime: number;
  clipEndTime: number;
  clipDuration: number;
  clipRelativeTime: number;
}

interface VideoPlayerContextValue {
  setupVideoElement: (element: HTMLVideoElement | null) => void;

  play: () => void;
  pause: () => void;
  togglePlay: () => void;
  seek: (time: number) => void;

  playbackState: RecordingPlaybackState;
  clipPlaybackState: ClipPlaybackState;
  isVideoReady: boolean;
  hasStartedPlaying: boolean;
}

const VideoPlayerContext = createContext<VideoPlayerContextValue | null>(null);

const CLIP_END_BUFFER_S = 1;

const PLAY_INTERRUPTED_MESSAGE =
  '[VideoPlayer] play() interrupted by pause() — expected when seek or clip-end runs';

function playWithCatch(video: HTMLVideoElement): void {
  const playPromise = video.play();
  if (playPromise !== undefined) {
    playPromise.catch((err: unknown) => {
      if (err instanceof DOMException && err.name === 'AbortError') {
        console.debug(PLAY_INTERRUPTED_MESSAGE);
      } else {
        console.error('[VideoPlayer] Unexpected play() error:', err);
      }
    });
  }
}

interface VideoPlayerProviderProps {
  children: ReactNode;
}

export const VideoPlayerProvider: React.FC<VideoPlayerProviderProps> = ({
  children,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const dispatch = useAppDispatch();
  const playbackState = useAppSelector(selectRecordingPlayback);
  const selectedClip = useAppSelector(selectSelectedClip);
  const aspectRatio = useAppSelector(selectAspectRatio);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [hasStartedPlaying, setHasStartedPlaying] = useState(false);

  // Clip mode is only active in portrait mode with a selected clip
  const isPortraitMode = aspectRatio === '9_16';

  // Calculate clip playback state (only active in portrait mode)
  const clipPlaybackState = useMemo((): ClipPlaybackState => {
    // Clip mode only applies in portrait mode with a selected clip
    if (!isPortraitMode || !selectedClip) {
      return {
        isClipMode: false,
        clipStartTime: 0,
        clipEndTime: playbackState.duration,
        clipDuration: playbackState.duration,
        clipRelativeTime: playbackState.currentTime,
      };
    }

    const clipStartTime = selectedClip.start_time_ms / 1000;
    const clipEndTime = selectedClip.end_time_ms / 1000;
    const clipDuration = clipEndTime - clipStartTime;
    const clipRelativeTime = Math.max(
      0,
      Math.min(clipDuration, playbackState.currentTime - clipStartTime),
    );

    return {
      isClipMode: true,
      clipStartTime,
      clipEndTime,
      clipDuration,
      clipRelativeTime,
    };
  }, [
    isPortraitMode,
    selectedClip,
    playbackState.currentTime,
    playbackState.duration,
  ]);

  // Handle clip end boundary - pause when reaching clip end.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !selectedClip) return;
    if (!isPortraitMode) return;

    const clipEndTime = selectedClip.end_time_ms / 1000;
    const clipStartTime = selectedClip.start_time_ms / 1000;
    const currentTime = video.currentTime;
    if (currentTime < clipEndTime - CLIP_END_BUFFER_S) return;

    if (playbackState.isPlaying && currentTime >= clipEndTime) {
      video.pause();
      video.currentTime = clipStartTime;
    }
  }, [
    isPortraitMode,
    playbackState.currentTime,
    playbackState.isPlaying,
    selectedClip,
  ]);

  // When clip is selected in portrait mode, seek to clip start
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !selectedClip || !isPortraitMode) return;

    const clipStartTime = selectedClip.start_time_ms / 1000;
    if (
      video.currentTime < clipStartTime ||
      video.currentTime > selectedClip.end_time_ms / 1000
    ) {
      video.currentTime = clipStartTime;
    }
  }, [selectedClip, isPortraitMode]);

  // Pending seek and play: after selecting a clip from the list play button, run seek+play
  // once the active player (portrait or landscape) has mounted and registered its element.
  useEffect(() => {
    if (!playbackState.pendingSeekAndPlay || !selectedClip) return;

    const id = setTimeout(() => {
      const video = videoRef.current;
      if (video && selectedClip) {
        const clipStartTime = selectedClip.start_time_ms / 1000;
        video.currentTime = clipStartTime;
        playWithCatch(video);
      }
      dispatch({ type: '[recording] clear pending seek and play' });
    }, 120);

    return () => clearTimeout(id);
  }, [playbackState.pendingSeekAndPlay, selectedClip, dispatch]);

  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    dispatch({
      type: '[recording] playback time updated',
      payload: {
        currentTime: video.currentTime,
        duration: video.duration || 0,
      },
    });
  }, [dispatch]);

  const handlePlayStateChange = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    const isPlaying = !video.paused;

    if (playbackState.isPlaying !== isPlaying) {
      dispatch({
        type: '[recording] play state changed',
        payload: {
          isPlaying,
        },
      });
    }
  }, [dispatch, playbackState.isPlaying]);

  const handleVideoReady = useCallback(() => {
    setIsVideoReady(true);
  }, []);

  const handleVideoLoading = useCallback(() => {
    setIsVideoReady(false);
    setHasStartedPlaying(false);
  }, []);

  const handleVideoPlay = useCallback(() => {
    setHasStartedPlaying(true);
    handlePlayStateChange();
  }, [handlePlayStateChange]);

  const handleVolumeChange = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    dispatch({
      type: '[recording] mute state changed',
      payload: { muted: video.muted },
    });
  }, [dispatch]);

  const attachEventListeners = useCallback(
    (video: HTMLVideoElement) => {
      video.addEventListener('timeupdate', handleTimeUpdate);
      video.addEventListener('play', handleVideoPlay);
      video.addEventListener('pause', handlePlayStateChange);
      video.addEventListener('playing', handlePlayStateChange);
      video.addEventListener('waiting', handlePlayStateChange);
      video.addEventListener('ended', handlePlayStateChange);
      video.addEventListener('canplay', handleVideoReady);
      video.addEventListener('loadstart', handleVideoLoading);
      video.addEventListener('volumechange', handleVolumeChange);
    },
    [
      handleTimeUpdate,
      handleVideoPlay,
      handlePlayStateChange,
      handleVideoReady,
      handleVideoLoading,
      handleVolumeChange,
    ],
  );

  const removeEventListeners = useCallback(
    (video: HTMLVideoElement) => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('play', handleVideoPlay);
      video.removeEventListener('pause', handlePlayStateChange);
      video.removeEventListener('playing', handlePlayStateChange);
      video.removeEventListener('waiting', handlePlayStateChange);
      video.removeEventListener('ended', handlePlayStateChange);
      video.removeEventListener('canplay', handleVideoReady);
      video.removeEventListener('loadstart', handleVideoLoading);
      video.removeEventListener('volumechange', handleVolumeChange);
    },
    [
      handleTimeUpdate,
      handleVideoPlay,
      handlePlayStateChange,
      handleVideoReady,
      handleVideoLoading,
      handleVolumeChange,
    ],
  );

  const setupVideoElement = useCallback(
    (element: HTMLVideoElement | null) => {
      if (videoRef.current) {
        removeEventListeners(videoRef.current);
      }
      (videoRef as any).current = element;

      if (element) {
        element.muted = playbackState.muted ?? false;
        element.volume = 1;
        attachEventListeners(element);
      }
    },
    [attachEventListeners, removeEventListeners, playbackState.muted],
  );

  // Control API functions
  const play = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    playWithCatch(video);
  }, []);

  const pause = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    video.pause();
  }, []);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      playWithCatch(video);
    } else {
      video.pause();
    }
  }, []);

  const seek = useCallback(
    (time: number) => {
      const video = videoRef.current;
      if (!video) return;

      // In clip mode (portrait with clip), constrain seeking within clip boundaries
      let constrainedTime = time;
      if (clipPlaybackState.isClipMode) {
        constrainedTime = Math.max(
          clipPlaybackState.clipStartTime,
          Math.min(clipPlaybackState.clipEndTime, time),
        );
      }

      video.pause();
      video.currentTime = constrainedTime;
    },
    [clipPlaybackState],
  );

  const contextValue = useMemo(
    (): VideoPlayerContextValue => ({
      setupVideoElement,
      play,
      pause,
      togglePlay,
      seek,
      playbackState,
      clipPlaybackState,
      isVideoReady,
      hasStartedPlaying,
    }),
    [
      setupVideoElement,
      play,
      pause,
      togglePlay,
      seek,
      playbackState,
      clipPlaybackState,
      isVideoReady,
      hasStartedPlaying,
    ],
  );

  return (
    <VideoPlayerContext.Provider value={contextValue}>
      {children}
    </VideoPlayerContext.Provider>
  );
};

export const useVideoPlayer = (): VideoPlayerContextValue => {
  const context = useContext(VideoPlayerContext);
  if (!context) {
    throw new Error('useVideoPlayer must be used within a VideoPlayerProvider');
  }
  return context;
};
